import { CopilotCitation } from "./copilotStudioClient"
import {
  isTableSeparatorRow,
  markdownUrl,
  normalizeMarkdownTables,
  splitTableCells,
} from "./copilotTables"

/**
 * LAB-113: claim-level citations. The agent writes [n] beside a claim; n is the
 * position of a citation entity (and of the `[n]: url "title"` footnote it appends).
 * Several positions can point at different passages of one document, so positions
 * are kept as they are instead of being merged by URL.
 */

/** Put after a sentence or table row that has no supporting document; rendered as a label. */
export const UNSUPPORTED_MARK = ""
export const UNSUPPORTED_LABEL = "Not tied to a cited document"

const FOOTNOTE = /^\s*\[(\d+)\]:\s*(\S+)(?:\s+"([^"]*)")?\s*$/
/** [n] that is not a markdown link label `[1](...)` or a footnote definition `[1]:`. */
const MARKER = /\[(\d{1,3})\](?![(:])/g

function decoded(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function urlPath(url: string) {
  try {
    return new URL(url).pathname
  } catch {
    return url.split(/[?#]/)[0]
  }
}

export function fileNameFromUrl(url: string) {
  return decoded(urlPath(url).split("/").pop() || "").trim()
}

/** "TA-Instruments-DCR-Brochure-EN (1).pdf" and "...EN.pdf" are one document. */
export function documentKey(url: string) {
  return fileNameFromUrl(url)
    .toLowerCase()
    .replace(/[\u2000-\u200B\u202F\u205F\u00A0]/g, " ")
    .replace(/\.[a-z0-9]{2,5}$/i, "")
    .replace(/\s*\(\d+\)$/, "")
    .replace(/\s+/g, " ")
    .trim()
}

export function sameDocumentUrl(left?: string, right?: string) {
  if (!left || !right) return false
  if (left === right) return true
  const key = documentKey(left)
  return key.length >= 6 && key === documentKey(right)
}

export function citationLabel(citation: CopilotCitation) {
  const title = (citation.title || "").trim()
  const fromUrl = citation.url ? fileNameFromUrl(citation.url) : ""
  if (title && !/^https?:\/\//i.test(title)) return title
  return fromUrl || title || "Source"
}

export function footnoteCitations(text: string): CopilotCitation[] {
  const found: CopilotCitation[] = []
  text.split("\n").forEach((line) => {
    const match = line.match(FOOTNOTE)
    if (!match || !/^https?:\/\//i.test(match[2])) return
    const position = Number(match[1])
    if (found.some((item) => item.position === position)) return
    found.push({
      title: (match[3] || "").trim() || fileNameFromUrl(match[2]) || match[2],
      url: match[2],
      position,
    })
  })
  return found
}

function bodyLines(text: string) {
  return text.split("\n").filter((line) => !FOOTNOTE.test(line))
}

/** The [n] numbers an answer uses, in order of first use. */
export function markerNumbers(text: string) {
  const numbers: number[] = []
  bodyLines(text).forEach((line) => {
    const pattern = new RegExp(MARKER.source, "g")
    let match = pattern.exec(line)
    while (match) {
      const value = Number(match[1])
      if (value > 0 && !numbers.includes(value)) numbers.push(value)
      match = pattern.exec(line)
    }
  })
  return numbers
}

/**
 * One citation per [n] marker: the entity positions first, then the footnotes the agent
 * appended. Chats saved before LAB-113 have neither positions nor footnotes in some
 * answers; their citations are matched to markers in order only when the counts agree.
 */
export function positionedCitations(
  text: string,
  citations: CopilotCitation[]
): CopilotCitation[] {
  const byPosition = new Map<number, CopilotCitation>()
  citations.forEach((citation) => {
    if (citation.position && !byPosition.has(citation.position)) {
      byPosition.set(citation.position, citation)
    }
  })
  footnoteCitations(text).forEach((footnote) => {
    const position = footnote.position as number
    const known = byPosition.get(position)
    if (!known) {
      byPosition.set(position, footnote)
    } else if (!known.url) {
      byPosition.set(position, { ...known, url: footnote.url })
    }
  })
  const unmatched = markerNumbers(text).filter(
    (number) => !byPosition.has(number)
  )
  const loose = citations.filter(
    (citation) => !citation.position && (citation.url || citation.title)
  )
  if (
    unmatched.length &&
    byPosition.size === 0 &&
    loose.length === unmatched.length
  ) {
    unmatched
      .sort((left, right) => left - right)
      .forEach((number, index) =>
        byPosition.set(number, { ...loose[index], position: number })
      )
  }
  return [...byPosition.values()].sort(
    (left, right) => (left.position || 0) - (right.position || 0)
  )
}

/**
 * Before LAB-113 a pasted SharePoint path with spaces was cut at the first space and saved
 * as a source ("https://dksh.sharepoint.com/teams/tec/Scientific"). Drop a link that is
 * only the start of another cited link.
 */
export function withoutTruncatedLinks(citations: CopilotCitation[]) {
  const urls = citations.map((citation) =>
    decoded(citation.url || "").toLowerCase()
  )
  return citations.filter((citation, index) => {
    const url = urls[index]
    return (
      !url ||
      !urls.some((other) => other.length > url.length && other.startsWith(url))
    )
  })
}

/** The citations to save with an answer: every marker's source, then any other cited link. */
export function citationsToSave(
  text: string,
  citations: CopilotCitation[]
): CopilotCitation[] {
  const positioned = positionedCitations(text, citations)
  const seen = new Set(positioned.map((item) => item.url).filter(Boolean))
  const others = withoutTruncatedLinks(citations).filter((citation) => {
    if (citation.position) return false
    const key = citation.url || citation.title
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
  return [...positioned, ...others]
}

const SENTENCE_END = /(?<=[.!?](?:\[\d+\])*)\s+/

/** The sentences (or table row) a marker stands beside, with the uncited ones just before it. */
export function claimsForMarker(text: string, position: number) {
  const claims: string[] = []
  bodyLines(text).forEach((line) => {
    const sentences = line.trim().split(SENTENCE_END)
    sentences.forEach((sentence, index) => {
      if (!markerNumbers(sentence).includes(position)) return
      let start = index
      while (
        start > 0 &&
        index - start < 2 &&
        !markerNumbers(sentences[start - 1]).length
      )
        start -= 1
      claims.push(
        sentences
          .slice(start, index + 1)
          .join(" ")
          .slice(0, 1400)
      )
    })
  })
  return claims.slice(0, 12)
}

/** SharePoint markers whose passage is not known yet. */
export function markersNeedingPassages(
  text: string,
  citations: CopilotCitation[]
) {
  return positionedCitations(text, citations).filter(
    (citation) =>
      citation.position &&
      citation.url &&
      !citation.excerpt &&
      /^https:\/\/[a-z0-9-]+\.sharepoint\.com\//i.test(citation.url) &&
      claimsForMarker(text, citation.position).length
  )
}

/** The saved citations with each found passage and page added to its marker. */
export function withPassages(
  text: string,
  citations: CopilotCitation[],
  found: Map<number, { excerpt?: string; page?: number }>
) {
  return citationsToSave(text, citations).map((citation) => {
    const passage = citation.position ? found.get(citation.position) : undefined
    return passage?.excerpt
      ? { ...citation, excerpt: passage.excerpt, page: passage.page }
      : citation
  })
}

const SHAREPOINT_FILE_URL =
  /https:\/\/[a-z0-9-]+\.sharepoint\.com\/[^\n<>"[\]]*?\.(?:pdf|docx?|pptx?|xlsx?|csv)(?=$|[\s)\],;!?'"*]|\.(?:\s|$))/gi

function linkRanges(line: string) {
  const ranges: Array<[number, number]> = []
  const pattern = /\[[^\]]*\]\([^)\s]*\)/g
  let match = pattern.exec(line)
  while (match) {
    ranges.push([match.index, match.index + match[0].length])
    match = pattern.exec(line)
  }
  return ranges
}

/**
 * Raw SharePoint links - often pasted with spaces in the path, which cut them short
 * at "Scientific" - become links named after the file.
 */
export function readableSourceLinks(text: string) {
  return text
    .split("\n")
    .map((line) => {
      if (FOOTNOTE.test(line)) return line
      const ranges = linkRanges(line)
      let next = ""
      let last = 0
      const pattern = new RegExp(SHAREPOINT_FILE_URL.source, "gi")
      let match = pattern.exec(line)
      while (match) {
        const start = match.index
        const end = start + match[0].length
        const inLink = ranges.some(([from, to]) => start >= from && start < to)
        if (!inLink) {
          next += `${line.slice(last, start)}[${fileNameFromUrl(
            match[0]
          )}](${markdownUrl(match[0])})`
          last = end
        }
        match = pattern.exec(line)
      }
      next += line.slice(last)
      // A link whose label is its own URL reads as a raw URL.
      return next.replace(
        /\[(https?:\/\/[^\]]+)\]\(([^)\s]+)\)/g,
        (whole, label: string, url: string) => {
          const name = fileNameFromUrl(url) || fileNameFromUrl(label)
          return name ? `[${name}](${url})` : whole
        }
      )
    })
    .join("\n")
}

function isTableLine(line: string) {
  const trimmed = line.trim()
  return (
    trimmed.startsWith("|") &&
    trimmed.endsWith("|") &&
    trimmed.split("|").length > 2
  )
}

function namesDocument(line: string, citation: CopilotCitation) {
  const lower = decoded(line).toLowerCase()
  const key = documentKey(citation.url || citation.title)
  return key.length >= 6 && lower.includes(key)
}

/** The table's own source: a heading or intro naming the document, or a closing line such as "All rows above are stated in ... [1]". */
function tableSourceMarkers(
  before: string[],
  after: string,
  positioned: CopilotCitation[]
) {
  let found: number[] = []
  before.some((line) => {
    const named = positioned.find((citation) => namesDocument(line, citation))
    const inline = markerNumbers(line)
    if (named?.position) found = [named.position]
    else if (
      inline.length &&
      /\b(table|below|following|values|rows?)\b/i.test(line)
    )
      found = inline.slice(0, 2)
    return found.length > 0
  })
  if (found.length) return found
  const closing = markerNumbers(after)
  if (closing.length && /\b(rows?|table|above)\b/i.test(after))
    return closing.slice(0, 2)
  return []
}

/**
 * LAB-113: a comparison table is cited once, under or above it. Its rows carry no
 * markers, so each row is given the markers of the table's source.
 */
export function citeTableRows(text: string, positioned: CopilotCitation[]) {
  if (!positioned.length) return text
  const lines = normalizeMarkdownTables(text).split("\n")
  let index = 0
  while (index < lines.length) {
    if (!isTableLine(lines[index])) {
      index += 1
    } else {
      const start = index
      while (index < lines.length && isTableLine(lines[index])) index += 1
      const rows = lines.slice(start, index)
      const dataRows = rows.slice(1).filter((row) => !isTableSeparatorRow(row))
      if (
        dataRows.length &&
        !dataRows.some((row) => markerNumbers(row).length)
      ) {
        const before = lines
          .slice(Math.max(0, start - 3), start)
          .filter((line) => line.trim())
          .reverse()
        const after = lines.slice(index).find((line) => line.trim()) || ""
        const markers = tableSourceMarkers(before, after, positioned)
        if (markers.length) {
          const suffix = markers.map((number) => `[${number}]`).join("")
          for (let row = start + 1; row < index; row += 1) {
            if (!isTableSeparatorRow(lines[row])) {
              const cells = splitTableCells(lines[row])
              cells[cells.length - 1] = `${
                cells[cells.length - 1]
              } ${suffix}`.trim()
              lines[row] = `| ${cells.join(" | ")} |`
            }
          }
        }
      }
    }
  }
  return lines.join("\n")
}
